import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'semantic-ui-react';

import { useStreaks } from '../../shared/api/apiHooks';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { hasConsultantPermission } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import analytics from '../../shared/services/analytics/analytics';
import { AppDispatch } from '../../shared/services/store/types';
import { updateCustomPreferencesState } from '../../reducers/CustomPreferencesReducer/CustomPreferencesReducer';
import { getStreakFreezeUnlockShownQuarter } from '../../reducers/CustomPreferencesReducer/CustomPreferencesSelectors';
import { ErrorBoundary } from '../ErrorBoundary';

import './StreakFreezeUnlockModal.scss';

// BRAINTECH-5836: one-time-per-quarter eligibility notice. Mounted app-level (next to GoldTierModal),
// shown the first time an eligible user (per BE `freeze.eligible`) loads the app in a quarter they
// haven't seen it yet. Dismissal is persisted cross-device via patchUserClientSettings
// (`streakFreezeUnlockShownQuarter`).
export function StreakFreezeUnlockModal() {
  const dispatch = useDispatch<AppDispatch>();
  // Only consultants are freeze-eligible, so gate the streak fetch on the consultant permission —
  // this modal mounts app-level (fires on every route), and there's no reason to hit
  // `/users/self/streak` for the majority of non-consultant users who can never see it.
  const isConsultant = useSelector(hasConsultantPermission);
  const { data } = useStreaks({ enabled: isConsultant });
  const shownQuarter = useSelector(getStreakFreezeUnlockShownQuarter);
  const [isOpen, setIsOpen] = useState(false);

  // Dedupe on the BE-provided quarter string. If BE omits `quarter` (a contract violation, since
  // `eligible` implies a populated freeze object), bail out rather than risk re-showing the modal
  // under an FE-derived key that may disagree with BE's quarter boundary around a rollover.
  const currentQuarter = data?.freeze?.quarter;
  const shouldShow = !!data?.freeze?.eligible && currentQuarter != null && shownQuarter !== currentQuarter;

  useEffect(() => {
    if (shouldShow) {
      setIsOpen(true);
      analytics.trackCustomEvent('streak_freeze_unlock_modal_shown');

      dispatch(updateCustomPreferencesState({ streakFreezeUnlockShownQuarter: currentQuarter }, { optimistic: true }));
    }
  }, [shouldShow, currentQuarter, dispatch]);

  const handleClose = () => {
    setIsOpen(false);
    analytics.trackCustomEvent('streak_freeze_unlock_modal_close');
  };

  const handleGotIt = () => {
    setIsOpen(false);
    analytics.trackCustomEvent('streak_freeze_unlock_modal_click_got_it');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ErrorBoundary showUI={false} isModal={true}>
      <Modal
        className="streak-freeze-unlock-modal"
        open={isOpen}
        onClose={handleClose}
        closeIcon={true}
        closeOnDimmerClick={false}
        size="tiny"
      >
        <Modal.Content>
          <div className="streak-freeze-unlock-modal__icon" aria-hidden="true" />
          <div className="streak-freeze-unlock-modal__title">
            <FormattedMessageHelper id="streakFreezeUnlock.modal.title" />
          </div>
          <div className="streak-freeze-unlock-modal__body">
            <FormattedMessageHelper id="streakFreezeUnlock.modal.body" />
          </div>
          <div className="streak-freeze-unlock-modal__genius-link">
            <FormattedMessageHelper id="streakFreezeUnlock.modal.geniusLink" />
          </div>
          <div className="streak-freeze-unlock-modal__actions">
            <button className="button button--primary" onClick={handleGotIt}>
              <FormattedMessageHelper id="streakFreezeUnlock.modal.gotIt" />
            </button>
          </div>
        </Modal.Content>
      </Modal>
    </ErrorBoundary>
  );
}
